import React, { ChangeEvent, useContext, useMemo } from 'react'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormFileField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { CreateLandingPage } from 'api/ClientArea/CreateLandingPage'
import { LandingPageDto } from 'api/ClientArea/LandingPageDto'
import { PromoMaterialsOptionsDto } from 'api/ClientArea/PromoMaterialsOptionsDto'
import { Language } from 'api/Clients/Types/Language'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { AuthSessionContext } from 'utils/AuthContext'
import { convertDateToUTC, createDateAsUTC } from 'utils/date'
import { toBase64 } from 'utils/file.helpers'
import { FormSubmitValues } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'

export interface IbLandingFormValues {
  name: string | undefined
  languageIds: string[]
  baseUrl: number | undefined
  path: string | undefined
  enabled: boolean
  endDateTime: string | undefined
  tickmillCompanyId: number | undefined
  landingPageImage: string | undefined
  landingPageFileImage: string | undefined
  order: number | undefined
  isDefault: boolean
}

type IbLandingFormProps = FormikProps<IbLandingFormValues> & OuterProps

const IbLandingFormUI: React.FC<IbLandingFormProps> = (props) => {
  const { handleSubmit, setFieldValue, errors, isSubmitting, isValid, values, onClose } = props
  const { tickmillCompanies, promoMaterialsOptions, languages } = props
  const [auth] = useContext(AuthSessionContext)

  const dateFormatType = auth?.user?.dateFormatType?.name
  const languageOptions = useMemo(
    () => languages.map((language) => ({ id: language.id, name: language.name })),
    [languages]
  )
  const baseUrlOptions = useMemo(
    () => promoMaterialsOptions.baseUrls.map((baseUrl) => ({ id: baseUrl.id, name: baseUrl.name })),
    [promoMaterialsOptions.baseUrls]
  )
  const companyOptions = useMemo(
    () => tickmillCompanies.map((company) => ({ id: company.id, name: company.name })),
    [tickmillCompanies]
  )

  const handleUploadImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files || []
    if (!files.length) {
      return
    }

    try {
      const [file] = files
      const base64 = await toBase64(file)
      if (file && base64) {
        await setFieldValue('landingPageFileImage', base64)
      }
    } catch (error) {
      if (error instanceof Error && error.message) {
        console.error('Error reading file:', error.message)
      }
    }
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{props.data?.id ? 'Edit Landing Page' : 'Add Landing Page'}</DialogTitle>
        </DialogHeader>
        <Form onSubmit={handleSubmit}>
          <div className='flex flex-col gap-4'>
            <FormField name='name' error={errors.name} label='Name' value={values.name} />
            <FormMultiSelectField
              name='languageIds'
              error={errors.languageIds?.toString()}
              label='Language'
              value={values.languageIds}
              options={languageOptions}
              showSearch
              allowClear
              onChange={(value) =>
                setFieldValue(
                  'languageIds',
                  Array.isArray(value) ? value.map(String) : value ? [String(value)] : []
                )
              }
            />
            <FormSelectField
              name='baseUrl'
              error={errors.baseUrl}
              label='Base URL'
              value={values.baseUrl}
              options={baseUrlOptions}
              showSearch
              allowClear
              onChange={(value) => setFieldValue('baseUrl', value ? Number(value) : undefined)}
            />
            <FormField name='path' error={errors.path} label='Path' value={values.path} />
            <FormFileField
              accept='image/jpeg'
              error={errors.landingPageFileImage}
              onChange={handleUploadImage}
              name='landingPageImage'
              label='Image'
            />
            <FormSelectField
              name='tickmillCompanyId'
              label='Tickmill Entity'
              value={values.tickmillCompanyId}
              options={companyOptions}
              showSearch
              allowClear
              error={errors.tickmillCompanyId}
              onChange={(value) =>
                setFieldValue('tickmillCompanyId', value ? Number(value) : undefined)
              }
            />
            <FormDateField
              name='endDateTime'
              error={errors.endDateTime}
              label='End Date'
              dateFormatType={dateFormatType}
            />
            <FormCheckboxField name='enabled' label='Active' value={values.enabled} />
          </div>
          <DialogFooter className='mt-6'>
            <Button disabled={isSubmitting} onClick={onClose} type='button' variant='outline'>
              Cancel
            </Button>
            <Button disabled={isSubmitting || !isValid} type='submit'>
              Save
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

const dataToSubmit = (values: Required<IbLandingFormValues>): CreateLandingPage => {
  return {
    name: values.name || '',
    promoMaterialBaseUrlId: values.baseUrl || 0,
    url: values.path || '',
    enabled: values.enabled || false,
    endDateTime: values.endDateTime
      ? createDateAsUTC(values.endDateTime, false, true).toISOString()
      : '',
    tickmillCompanyId: values.tickmillCompanyId || 0,
    languageIds: values.languageIds || [],
    landingPageImage: values.landingPageFileImage || '',
    order: 99999,
    isDefault: false,
  }
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  data?: LandingPageDto
  languages: Language[]
  tickmillCompanies: TickmillCompanyDto[]
  promoMaterialsOptions: PromoMaterialsOptionsDto
  onSubmit(values: FormSubmitValues<CreateLandingPage>): Promise<void>
}

const getDefaultCompanyId = (companies: TickmillCompanyDto[]): TickmillCompanyTypes | undefined => {
  return companies.find((x) => x.id === TickmillCompanyTypes.Tickmill_SC_Company)?.id
}

const getDefaultLanguage = (languages: Language[]): NameDto<string> | undefined => {
  return languages.find((x) => x.id === 'en')
}

export const IbLandingForm = withFormik<OuterProps, IbLandingFormValues>({
  mapPropsToValues: (props) => {
    const { data } = props
    const defaultLanguage = getDefaultLanguage(props.languages)
    const defaultCompany = getDefaultCompanyId(props.tickmillCompanies)
    const initialLanguages = data?.languages ?? (defaultLanguage ? [defaultLanguage] : [])
    const initialEndDateTime = data?.endDateTime

    return {
      name: data?.name || undefined,
      languageIds: initialLanguages.map((x) => x.id),
      baseUrl: data?.promoMaterialBaseUrl?.id || undefined,
      path: data?.url || undefined,
      enabled: data?.enabled || false,
      endDateTime: initialEndDateTime
        ? convertDateToUTC(initialEndDateTime).toISOString()
        : undefined,
      tickmillCompanyId: data?.tickmillCompany?.id || defaultCompany,
      landingPageImage: undefined,
      landingPageFileImage: undefined,
      order: data?.order || undefined,
      isDefault: data?.isDefault || false,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<IbLandingFormValues> = {}

    if (!values.name) {
      errors.name = 'Required'
    }

    if (values.languageIds.length <= 0) {
      errors.languageIds = 'Required'
    }

    if (!values.baseUrl) {
      errors.baseUrl = 'Required'
    }

    if (!values.path) {
      errors.path = 'Required'
    }

    if (!values.landingPageFileImage) {
      errors.landingPageFileImage = 'Required'
    }

    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Required'
    }

    if (isEmpty(values.enabled)) {
      errors.enabled = 'Required'
    }

    return errors
  },
  enableReinitialize: false,
  validateOnChange: true,
})(IbLandingFormUI)
